import React, { useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";
import { useDispatch, useSelector } from "react-redux";
import {
  Card,
  Flex,
  Heading,
  Spacer,
  Text,
  Td,
  Tr,
} from "@chakra-ui/react";
import CustomButton from "../../components/ButtonComp";
import Loader from "../../components/Loader";
import TableView from "../../components/TableView";
import CustomSingleInput from "../../components/CustomSingleInput";
import { get_bank_reports } from "../../redux/bankReportSlice";
import { get_bank_list } from "../../redux/bankSlice";

const BankReports = () => {
  // Redux selectors
  const { bank_list } = useSelector((state) => state.banks);
  const { bank_reports, loading, total_count } = useSelector((state) => state.bankReports);

  const navigate = useNavigate();
  const dispatch = useDispatch();
  
  const [rows, setRows] = useState(5);
  const [page, setPage] = useState(1);
  const [selectedBank, setSelectedBank] = useState("");
  
  // Date filter states
  const [startDate, setStartDate] = useState("");
  const [endDate, setEndDate] = useState("");
  const [appliedFilters, setAppliedFilters] = useState({
    startDate: "",
    endDate: "",
    selectedBank: ""
  });

  // Load bank list on component mount
  useEffect(() => {
    dispatch(get_bank_list({ page: 1, rows: 100 }));
  }, [dispatch]);

  const get_bank_report_list = async () => {
    const params = {
      page: page,
      limit: rows,
      action: "list",
    };

    // Add optional parameters only if they have values
    if (appliedFilters.startDate) params.fd = appliedFilters.startDate;
    if (appliedFilters.endDate) params.td = appliedFilters.endDate;
    if (appliedFilters.selectedBank) params.bank_id = appliedFilters.selectedBank;
    
    console.log("API params:", params);
    
    await dispatch(get_bank_reports(params));
  };

  // Fetch data when page, rows, or applied filters change
  useEffect(() => {
    get_bank_report_list();
  }, [page, rows, appliedFilters]);

  // Initial load
  useEffect(() => {
    // Load initial data
    dispatch(get_bank_reports({ page: 1, limit: rows, action: "list" }));
  }, [dispatch, rows]);

  // Handle search button click
  const handleSearch = () => {
    setAppliedFilters({
      startDate,
      endDate,
      selectedBank
    });
    setPage(1); // Reset to first page
  };

  // Handle bank selection change
  const handleBankChange = (e) => {
    const bankId = e.target.value;
    setSelectedBank(bankId);
  };

  // Handle rows per page change
  const handleRowsChange = (e) => {
    const newRows = Number(e.target.value);
    setRows(newRows);
    setPage(1); // Reset to first page when rows change
  };

  // Handle previous page
  const handlePrevious = () => {
    if (page > 1) {
      setPage(page - 1);
    }
  };

  // Handle next page
  const handleNext = () => {
    const totalPages = Math.ceil(total_count / rows);
    if (page < totalPages) {
      setPage(page + 1);
    }
  };

  // Handle direct page navigation
  const handlePageChange = (newPage) => {
    const totalPages = Math.ceil(total_count / rows);
    if (newPage >= 1 && newPage <= totalPages) {
      setPage(newPage);
    }
  };

  // Calculate the starting index for serial numbers
  const getStartingIndex = () => {
    return (page - 1) * rows + 1;
  };

  // Calculate pagination info
  const totalPages = Math.ceil(total_count / rows);
  const startRecord = (page - 1) * rows + 1;
  const endRecord = Math.min(page * rows, total_count);

  // Bank options for dropdown
  const bankOptions = bank_list?.map((bank) => ({
    label: bank.bankname,
    value: bank.id,
  })) || [];

  return (
    <>
      {/* Header Card */}
      <Card
        bg="#fff"
        py={3}
        px={5}
        flexDirection={["column", "row"]}
        alignItems={["flex-start", "center"]}
        gap={3}
        flexWrap="wrap"
      >
        <Heading
          display="flex"
          w="full"
          alignItems="center"
          justifyContent="space-between"
          fontSize="xl"
        >
          Bank Reports
        </Heading>
        <Spacer display={["none", "block"]} />
      </Card>

      {/* Filter Card */}
      <Card bg="#fff" py={3} px={5} gap={3}>
        <Flex
          direction={["column", "row"]}
          gap={3}
          flexWrap="wrap"
          w="full"
          justify="flex-start"
        >
          <CustomSingleInput
            width={["100%", "200px"]}
            label="Start Date"
            type="date"
            value={startDate}
            onChange={(e) => setStartDate(e.target.value)}
          />
          <CustomSingleInput
            width={["100%", "200px"]}
            label="End Date"
            type="date"
            value={endDate}
            onChange={(e) => setEndDate(e.target.value)}
          />
          <CustomSingleInput
            label="Bank"
            width={["100%", "200px"]}
            placeholder="Select Bank"
            select
            options={bankOptions}
            value={selectedBank}
            onChange={handleBankChange}
          />
          <CustomButton
            title="Search"
            w={["100%", "auto"]}
            onClick={handleSearch}
          />
        </Flex>

        {/* Display current applied filters */}
        {(appliedFilters.startDate || appliedFilters.endDate) && (
          <Text>
            {appliedFilters.startDate && (
              <>From Date: <b>{appliedFilters.startDate}</b></>
            )}
            {appliedFilters.startDate && appliedFilters.endDate && " "}
            {appliedFilters.endDate && (
              <>To Date: <b>{appliedFilters.endDate}</b></>
            )}
          </Text>
        )}

        {/* Pagination Info */}
        {total_count > 0 && (
          <Text fontSize="sm" color="gray.600">
            Showing {startRecord} to {endRecord} of {total_count} entries
            {totalPages > 1 && ` (Page ${page} of ${totalPages})`}
          </Text>
        )}
      </Card>

      {/* Results Card */}
      {loading ? (
        <Card justifyContent="center" alignItems="center" p={5}>
          <Loader />
        </Card>
      ) : bank_reports?.length === 0 ? (
        <Card justifyContent="center" alignItems="center" h={20}>
          <Text>No Data Found</Text>
        </Card>
      ) : (
        <Card>
          <TableView
            headData={[
              "S.No.",
              "Order No.",
              "Voucher No.",
              "Voucher Date",
              "Amount",
              "Payment Mode",
              "Transaction No",
              "Transaction Mode",
              "Cash received",
              "Online received",
              "Credit",
              "Debit",
              "Amount",
              "Closing Balance",
            ]}
            body={bank_reports?.map((el, i) => (
              <Tr key={`${page}-${i}`}>
                <Td textAlign="center">{getStartingIndex() + i}</Td>
                <Td textAlign="center">{el.orderids || 'NA'}</Td>
                <Td textAlign="center">{el.sr_no || 'NA'}</Td>
                <Td textAlign="center">{el.voucher_date || 'NA'}</Td>
                <Td textAlign="center">₹{el.amount || '0.00'}</Td>
                <Td textAlign="center">{el.payment_mode || 'NA'}</Td>
                <Td textAlign="center">{el.transaction_number || 'NA'}</Td>
                <Td textAlign="center">{el.mode_of_transaction || 'NA'}</Td>
                <Td textAlign="center">₹{el.cash_received || '0.00'}</Td>
                <Td textAlign="center">₹{el.online_received || '0.00'}</Td>
                <Td textAlign="center">₹{el.online_received || '0.00'}</Td>
                <Td textAlign="center">₹{el.debit || '0.00'}</Td>
                <Td textAlign="center">₹{el.amount || '0.00'}</Td>
                <Td textAlign="center">₹{el.closing_balance || '0.00'}</Td>
              </Tr>
            ))}
            // Pagination props
            page={page}
            rows={rows}
            total_value={total_count}
            totalPages={totalPages}
            pagination={true}
            // Pagination handlers
            onChange={handleRowsChange}
            onPrevious={handlePrevious}
            onNext={handleNext}
            onPageChange={handlePageChange}
            // Additional pagination info
            startRecord={startRecord}
            endRecord={endRecord}
          />
        </Card>
      )}
    </>
  );
};

export default BankReports;